import { ActivityItem } from "../components/types";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { ContactItem, VerificationStatus } from "../views/crm-pages/customers/types";

export function formatCurrency(
  value: number | undefined | null,
  currency?: string,
  display?: "code" | "symbol"
) {
  try {
    if (value != null) {
      return value.toLocaleString("en-NG", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        currencyDisplay: display || undefined,
      });
    } else {
      return "amount must be 200 above and less than 5,000,000";
    }
  } catch (e) {

    return "N/A";
  }
}

export const formatTime = (isoDate: string): string => {
  const date = new Date(isoDate);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export function formatDateLong(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  const formattedDate = date?.toLocaleString("en-US", options).toUpperCase();
  return formattedDate?.replace(",", "");
};

export const formatDateLong2 = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

export const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
};

export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const inputDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  const timeString = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });

  // Today
  if (inputDate.getTime() === today.getTime()) {
    return `Today at ${timeString}`;
  }

  // Yesterday
  if (inputDate.getTime() === yesterday.getTime()) {
    return `Yesterday at ${timeString}`;
  }

  // Within last 7 days
  if (diffInDays < 7 && diffInDays > 1) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return `${days[date.getDay()]} at ${timeString}`;
  }

  // Older dates
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export function signOut() {
  // Clear localStorage
  localStorage.removeItem("authToken");
  localStorage.removeItem("user");
}
// Map status to display text
export const getInitials = (title: string): string => {
  if (!title) return "?";
  if (title.match(/^[\+\d\s\-]+$/)) {
    return title.replace(/\D/g, '')?.slice(-2) || "P";
  }
  return title
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    ?.slice(0, 2);
};

export const getColorFromId = (id: string): string => {
  const colors = ["#6366f1", "#f43f5e", "#f59e0b", "#10b981", "#8b5cf6", "#ec4899", "#14b8a6", "#3b82f6", "#f97316"];
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

export const getStatusBadge = (status: string): { label: string; className: string } => {
  switch (status) {
    case "open":
      return { label: "Open", className: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-400" };
    case "closed":
      return { label: "Closed", className: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" };
    case "pending":
      return { label: "Pending", className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-400" };
    default:
      return { label: status, className: "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-400" };
  }
};

// WhatsApp's 24h customer-service window — from `whatsapp_window_expires_at`/
// `whatsapp_window_status` on a Conversation. Null only when there's no
// window data at all. Once the deadline passes (or the backend already says
// "closed"), flips to a "Closed" state client-side rather than waiting on
// the next fetch — the countdown alone tells us it's stale.
export const formatWhatsAppWindowCountdown = (
  expiresAt?: string | null,
  status?: string | null,
): { label: string; urgent: boolean; closed: boolean } | null => {
  if (!expiresAt) return null;
  const expiryMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiryMs)) return null;

  const diffMs = expiryMs - Date.now();
  if (status === "closed" || diffMs <= 0) {
    return { label: "Closed", urgent: false, closed: true };
  }

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const label = hours > 0
    ? (minutes > 0 ? `${hours}h ${minutes}m left` : `${hours}h left`)
    : `${minutes}m left`;

  // <=1h left: red/urgent. >1h left: green.
  return { label, urgent: diffMs <= 60 * 60 * 1000, closed: false };
};



export const getStatusColors = (status: string): string => {
  switch (status) {
    case "APPROVED":
      return "green";
    case "PENDING":
      return "yellow";
    case "REJECTED":
      return "red";
    default:
      return "gray";
  }
};


export const getTimeBasedGreeting = () => {
  const currentHour = new Date().getHours();

  if (currentHour >= 5 && currentHour < 12) {
    return "Good morning";
  } else if (currentHour >= 12 && currentHour < 17) {
    return "Good afternoon";
  } else if (currentHour >= 17 && currentHour < 21) {
    return "Good evening";
  } else {
    return "Good night";
  }
};


export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}


// Map status to display text
export const getDisplayStatus = (status?: string): VerificationStatus => {
  if (!status) return "Pending";

  switch (status.toLowerCase()) {
    case "email_verified":
      return "Verified";
    case "phone_verified":
      return "Pending";
    case "otp_pending":
      return "Pending";
    case "verified":
      return "Verified";
    case "unverified":
      return "Unverified";
    case "pending":
      return "Pending";
    case "active":
      return "Verified";
    default:
      return "Pending";
  }
};

// Get status color for badge
export const getStatusCustomerColors = (status?: string): string => {
  if (!status) return "bg-[#FEF0CD] dark:bg-yellow-500/15 text-[#3A3428] dark:text-yellow-400 border-yellow-200 dark:border-yellow-500/40";

  switch (status.toLowerCase()) {
    case "email_verified":
      return "bg-[#E7FECC] dark:bg-blue-500/15 text-[#3A3428] dark:text-blue-400 border-blue-200 dark:border-blue-500/40";
    case "phone_verified":
      return "bg-[#FEF0CD] dark:bg-green-500/15 text-[#3A3428] dark:text-green-400 border-green-200 dark:border-green-500/40";
    case "otp_pending":
      return "bg-[#FEF0CD] dark:bg-orange-500/15 text-[#3A3428] dark:text-orange-400 border-orange-200 dark:border-orange-500/40";
    case "customer":
      return "bg-[#FEF0CD] dark:bg-orange-500/15 text-[#3A3428] dark:text-orange-400 border-orange-200 dark:border-orange-500/40";
    case "verified":
      return "bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/40";
    case "unverified":
      return "bg-[#FEF0CD] dark:bg-gray-800 text-[#3A3428] dark:text-gray-300 border-gray-200 dark:border-gray-700";
    case "active":
      return "bg-[#E7FECC] dark:bg-green-500/15 text-[#3A3428] dark:text-green-400 border-[#CDE7A4] dark:border-green-500/40";
    case "pending":
      return "bg-yellow-50 dark:bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-500/40";
    case "onboarded":
      return "bg-[#EAF4FF] dark:bg-blue-500/15 text-[#3A3428] dark:text-blue-400 border-[#B8D9FF] dark:border-blue-500/40";
    case "waitlist":
      return "bg-[#FEF0CD] dark:bg-yellow-500/15 text-[#3A3428] dark:text-yellow-400 border-[#F4D58D] dark:border-yellow-500/40";
    default:
      return "bg-[#FEF0CD] dark:bg-gray-800 text-[#3A3428] dark:text-gray-300 border-gray-200 dark:border-gray-700";
  }
};

// Get status dot color
export const getStatusDotColor = (status?: string): string => {
  if (!status) return "bg-yellow-400";

  switch (status.toLowerCase()) {
    case "email_verified":
      return "bg-[#22C55E]";
    case "phone_verified":
      return "bg-[#FBBF24]";
    case "otp_pending":
      return "bg-[#FBBF24]";
    case "verified":
      return "bg-green-500";
    case "unverified":
      return "bg-[#FBBF24]";
    case "active":
      return "bg-green-500";
    case "pending":
      return "bg-yellow-500";
    default:
      return "bg-[#FBBF24]";
  }
};

export const getAccountStatusColor = (status: string): string => {
  switch (status) {
    case "active":
      return "bg-[#E7FECC] dark:bg-green-500/15 text-[#3A3428] dark:text-green-400 border-[#CDE7A4] dark:border-green-500/40";
    case "onboarded":
      return "bg-[#EAF4FF] dark:bg-blue-500/15 text-[#3A3428] dark:text-blue-400 border-[#B8D9FF] dark:border-blue-500/40";
    case "blocked":
      return "bg-[#FEE2E2] dark:bg-red-500/15 text-[#3A3428] dark:text-red-400 border-[#FECACA] dark:border-red-500/40";
    case "waitlist":
      return "bg-[#FEF0CD] dark:bg-yellow-500/15 text-[#3A3428] dark:text-yellow-400 border-[#F4D58D] dark:border-yellow-500/40";
    default:
      return "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700";
  }
};

export const getAccountStatusFromCustomer = (
  customer: ContactItem
): string => {
  const status = customer.custom?.status?.toLowerCase();
  const tags = customer.tags || [];

  const isBlocked = tags.includes("blocked");
  const hasVouchlink = tags.includes("vouchlink:true");

  if (isBlocked) {
    return "blocked";
  }

  if (hasVouchlink && !isBlocked) {
    return "active";
  }

  // A customer explicitly tagged "onboarded" is onboarded regardless of
  // what custom.status says or which other tags are present.
  if (tags.includes("onboarded")) {
    return "onboarded";
  }

  if (customer.tags?.includes("blocked")) {
    return "blocked";
  }

  if (customer.tags?.includes("waitlist")) {
    return "waitlist";
  }

  if (customer.tags?.includes("otp_pending")) {
    return "unverified";
  }

  if (customer.tags?.includes("phone_verified")) {
    return "unverified";
  }

  if (customer.tags?.includes("email_verified")) {
    return "onboarded";
  }

  return "unverified";
};

export const renderTextWithLinks = (text: string) => {
  const urlRegex = /((https?:\/\/|www\.)[^\s]+)/g;

  const elements: React.ReactNode[] = [];
  let lastIndex = 0;

  const matches = text.matchAll(urlRegex);

  for (const match of matches) {
    const url = match[0];
    const index = match.index ?? 0;

    // Add text before the link
    if (index > lastIndex) {
      elements.push(
        <span key={lastIndex}>{text.slice(lastIndex, index)}</span>
      );
    }

    // Fix href for www links
    const href = url.startsWith("www.") ? `https://${url}` : url;

    // Add the link
    elements.push(
      <a
        key={index}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#4338CA] underline break-all"
      >
        {url}
      </a>
    );

    lastIndex = index + url.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    elements.push(
      <span key={lastIndex}>{text.slice(lastIndex)}</span>
    );
  }

  return elements;
};

// utils/navigation.ts
export function navigateToConversation(conversationId: string) {
  // Dispatch custom event for React Router
  window.dispatchEvent(new CustomEvent('react-router-navigate', {
    detail: { to: `/conversations/${conversationId}` }
  }));

  // Also try to update URL directly as fallback
  const currentPath = window.location.pathname;
  if (!currentPath.includes(`/conversations/${conversationId}`)) {
    window.history.pushState({}, '', `/conversations/${conversationId}`);
    // Dispatch popstate event to notify React Router
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

export const formatFixedTime = (value?: string) => {
  if (!value) return "";
  if (!value.includes("T")) return value;

  const [datePart, rawTime = "00:00"] = value.split("T");
  const [year, month, day] = datePart.split("-");
  const [hourRaw, minute = "00"] = rawTime.split(":");
  const hour = Number(hourRaw);
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;

  return `${month}-${day}-${year} ${String(displayHour).padStart(2, "0")}:${minute}:00${period}`;
};

export const parseRecipients = (value?: string | string[]) =>
  Array.isArray(value)
    ? value
    : (value ?? "")
      .split(/\r?\n|,/)
      .map((recipient) => recipient.trim())
      .filter(Boolean);

// FastAPI validation errors come back as `detail: [{msg, ...}]` (e.g. "Value
// error, Previews can only be sent to a maximum of 2 recipients at a time"),
// other failures as a string `detail` or `message` — turn either into text.
export const apiErrorMessage = (response: any, fallback: string): string => {
  const detail = response?.detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((d: any) => String(d?.msg || "").replace(/^Value error,\s*/, "")).filter(Boolean);
    if (msgs.length) return msgs.join(" ");
  }
  if (typeof detail === "string" && detail) return detail;
  if (typeof response?.message === "string" && response.message) return response.message;
  return fallback;
};
